// Difficulty ladder for the in-browser bot (plan §9). Each level maps to a
// Stockfish "Skill Level" (0–20, the engine's own weakening knob) plus a search
// cap so low levels also think shallowly — the lila-style skill/depth/movetime
// table. `movetimeMs` is a safety ceiling; `depth` is what usually bounds a move.

import { t } from "../i18n/index.ts";

export interface BotLevel {
  level: number; // 1–8
  label: string; // English fallback; UI uses botLevelLabel() for the localized form
  skill: number; // UCI "Skill Level", 0–20
  depth: number; // search depth cap
  movetimeMs: number; // wall-clock cap per move
}

export const BOT_LEVELS: BotLevel[] = [
  { level: 1, label: "Level 1 · beginner", skill: 0, depth: 5, movetimeMs: 200 },
  { level: 2, label: "Level 2", skill: 3, depth: 5, movetimeMs: 300 },
  { level: 3, label: "Level 3", skill: 6, depth: 6, movetimeMs: 400 },
  { level: 4, label: "Level 4 · club", skill: 9, depth: 8, movetimeMs: 600 },
  { level: 5, label: "Level 5", skill: 11, depth: 10, movetimeMs: 800 },
  { level: 6, label: "Level 6 · strong", skill: 14, depth: 12, movetimeMs: 1200 },
  { level: 7, label: "Level 7", skill: 17, depth: 16, movetimeMs: 1800 },
  { level: 8, label: "Level 8 · maximum", skill: 20, depth: 22, movetimeMs: 2500 },
];

/** Clamp `n` to a valid level and return its config (default: level 3). */
export function botLevel(n: number): BotLevel {
  return BOT_LEVELS.find((l) => l.level === n) ?? BOT_LEVELS[2];
}

// A qualifier word for the notable rungs (others show the bare level number).
const QUALIFIER: Record<number, string> = {
  1: "levels.beginner",
  4: "levels.club",
  6: "levels.strong",
  8: "levels.maximum",
};

/** Localized display label for a bot level, e.g. "Level 1 · beginner". */
export function botLevelLabel(n: number): string {
  const lvl = botLevel(n).level;
  const q = QUALIFIER[lvl];
  return q ? t("levels.levelNq", { n: lvl, q: t(q) }) : t("levels.levelN", { n: lvl });
}
